import {Avatar} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'


/**
 * AvatarButton component renders an Avatar with various customizable properties.
 *
 * @param {Object} props - The component props
 * @param {Function} [props.onClick] - Function to handle click events
 * @param {string} [props.name=''] - The name to display in the avatar
 * @param {('xSmall'|'small'|'medium'|'large')} [props.size='medium'] - The size of the avatar
 * @param {('text'|'image'|'icon')} [props.type='icon'] - The type of the avatar
 * @param {('circle'|'square')} [props.avatarStyle='circle'] - The style of the avatar
 * @param {boolean} [props.showSkeleton=false] - Whether to show a skeleton loader
 * @param {string} [props.imageUrl=null] - The URL of the avatar image
 * @param {boolean} [props.showEditIcon=false] - Whether to show an edit icon
 * @param {Function} [props.onClickArrow] - Function to handle arrow click events
 * @param {boolean} [props.disabled=null] - Whether the avatar button is disabled
 * @param {('person'|'business')} [props.iconType='person'] - The type of icon to display
 * @param {('online'|'offline'|'notAvailable')} [props.status=null] - The status to display on the avatar
 * @param {boolean} [props.showStatus=null] - Whether to show the status
 * @param {boolean} [props.showArrowIcon=null] - Whether to show an arrow icon
 * @param {boolean} [props.showTooltip=null] - Whether to show a tooltip
 * @returns {JSX.Element} The rendered AvatarButton component
 */
export default function AvatarButton({
  onClick,
  name = '',
  size = 'medium',
  type = 'icon',
  avatarStyle = 'circle',
  showSkeleton = false,
  imageUrl = null,
  showEditIcon = true,
  onClickArrow,
  disabled = null,
  iconType = 'person',
  status = null,
  showStatus = null,
  showArrowIcon = false,
  showTooltip = null,
}) {
  return (
    <span data-testid="avatar-button">
      <Avatar
        name={name}
        onClick={onClick}
        size={size}
        type={type}
        avatarStyle={avatarStyle}
        showSkeleton={showSkeleton}
        imageUrl={imageUrl}
        showEditIcon={showEditIcon}
        onClickArrow={onClickArrow}
        iconType={iconType}
        status={status}
        showStatus={showStatus}
        showArrowIcon={showArrowIcon}
        showTooltip={showTooltip}
        disabled={disabled}
      />
    </span>
  )
}

AvatarButton.propTypes = {
  onClick: PropTypes.func,
  name: PropTypes.string,
  size: PropTypes.oneOf(['xSmall', 'small', 'medium', 'large']),
  type: PropTypes.oneOf(['text', 'image', 'icon']),
  avatarStyle: PropTypes.oneOf(['circle', 'square']),
  showSkeleton: PropTypes.bool,
  imageUrl: PropTypes.string,
  showEditIcon: PropTypes.bool,
  onClickArrow: PropTypes.func,
  disabled: PropTypes.bool,
  iconType: PropTypes.oneOf(['person', 'business']),
  status: PropTypes.oneOf(['online', 'offline', 'notAvailable']),
  showStatus: PropTypes.bool,
  showArrowIcon: PropTypes.bool,
  showTooltip: PropTypes.bool,
}

